import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { MapPin, Search, Loader2, Building } from 'lucide-react';
import { Header } from '@/components/layout/Header';
import { TownNavBar } from '@/components/layout/TownNavBar';
import { AreaNavBar } from '@/components/layout/AreaNavBar';
import { Footer } from '@/components/layout/Footer';

interface TownData {
  id: string;
  name: string;
  slug: string;
  state: string | null;
  intro_summary: string | null;
  _areaCount?: number;
}

interface SiteData {
  id: string;
  name: string;
  location_served: string;
  favicon_url?: string | null;
  enable_market_value_report?: boolean;
  enable_community_guide?: boolean;
  enable_mortgage_info?: boolean;
}

interface RealtorData {
  first_name: string | null;
  last_name: string | null;
  photo_url: string | null;
  phone: string | null;
  city: string | null;
  state: string | null;
  license_numbers: string | null;
  about_me: string | null;
  logo_url?: string | null;
}

interface Area {
  id: string;
  name: string;
  slug: string;
}

export default function TownsIndex() {
  const [site, setSite] = useState<SiteData | null>(null);
  const [towns, setTowns] = useState<TownData[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [realtor, setRealtor] = useState<RealtorData | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchTowns();
  }, []);

  const fetchTowns = async () => {
    setLoading(true);
    try {
      // Resolve site by hostname for multi-tenancy
      const hostname = window.location.hostname;
      const isDevelopment = 
        hostname === 'localhost' || 
        hostname.includes('lovable.app') ||
        hostname.includes('127.0.0.1');

      let siteData: SiteData | null = null;

      if (!isDevelopment) {
        // Production: Try to match by primary_domain
        const hostWithoutWww = hostname.replace(/^www\./, '');
        const hostWithWww = hostname.startsWith('www.') ? hostname : `www.${hostname}`;

        const { data: domainMatch } = await supabase
          .from('sites_public' as any)
          .select('id, name, location_served, favicon_url, enable_market_value_report, enable_community_guide, enable_mortgage_info')
          .eq('status', 'active')
          .or(`primary_domain.eq.${hostname},primary_domain.eq.${hostWithoutWww},primary_domain.eq.${hostWithWww}`)
          .limit(1)
          .maybeSingle();

        if (domainMatch) {
          siteData = domainMatch as unknown as SiteData;
          console.log('Towns index: Site resolved by domain:', hostname);
        }
      }

      // Fallback to first active site (by created_at) for development
      if (!siteData) {
        const { data: fallbackSite } = await supabase
          .from('sites_public' as any)
          .select('id, name, location_served, favicon_url, enable_market_value_report, enable_community_guide, enable_mortgage_info')
          .eq('status', 'active')
          .order('created_at', { ascending: true })
          .limit(1)
          .maybeSingle();

        siteData = fallbackSite as unknown as SiteData;
      }

      if (siteData) {
        setSite(siteData);

        // Get published towns
        const { data: townData } = await supabase
          .from('towns')
          .select('id, name, slug, state, intro_summary')
          .eq('site_id', siteData.id)
          .eq('is_published', true)
          .order('name');

        if (townData) {
          // Get area counts and realtor data in parallel
          const [townsWithCounts, realtorRes] = await Promise.all([
            Promise.all(
              townData.map(async (town) => {
                const { count } = await supabase
                  .from('areas')
                  .select('id', { count: 'exact', head: true })
                  .eq('town_id', town.id)
                  .eq('is_published', true);
                return { ...town, _areaCount: count || 0 };
              })
            ),
            supabase
              .from('realtor_profiles')
              .select('first_name, last_name, photo_url, phone, city, state, license_numbers, about_me, logo_url')
              .eq('site_id', siteData.id)
              .single()
          ]);
          
          setTowns(townsWithCounts);
          if (realtorRes.data) setRealtor(realtorRes.data);
          
          // If only one town, fetch its areas for the AreaNavBar
          if (townsWithCounts.length === 1) {
            const { data: areasData } = await supabase
              .from('areas')
              .select('id, name, slug')
              .eq('town_id', townsWithCounts[0].id)
              .eq('is_published', true)
              .order('name');
            
            if (areasData) setAreas(areasData);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching towns:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTowns = towns.filter(town =>
    town.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (town.state && town.state.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Loading...</title>
          <meta name="description" content="" />
          <meta property="og:title" content="" />
          <meta property="og:description" content="" />
          <meta name="twitter:title" content="" />
          <meta name="twitter:description" content="" />
        </Helmet>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>All Towns | {site?.name || 'Real Estate'}</title>
        <meta name="description" content={site?.location_served ? `Browse all towns and real estate in ${site.location_served}` : 'Browse all towns and real estate'} />
        <link rel="canonical" href={`${window.location.origin}/towns`} />
        <meta name="robots" content="index, follow" />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={site?.name || 'Real Estate'} />
        <meta property="og:title" content={`All Towns | ${site?.name || 'Real Estate'}`} />
        <meta property="og:description" content={site?.location_served ? `Browse all towns and real estate in ${site.location_served}` : 'Browse all towns and real estate'} />
        <meta property="og:url" content={`${window.location.origin}/towns`} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={`All Towns | ${site?.name || 'Real Estate'}`} />
        <meta name="twitter:description" content={site?.location_served ? `Browse all towns and real estate in ${site.location_served}` : 'Browse all towns and real estate'} />
      </Helmet>
      <Header 
        siteName={site?.name || 'Real Estate'} 
        logoUrl={realtor?.logo_url}
        faviconUrl={site?.favicon_url}
        enableMarketValueReport={site?.enable_market_value_report !== false}
        enableCommunityGuide={site?.enable_community_guide !== false}
        enableMortgageInfo={site?.enable_mortgage_info !== false}
      />
      
      {/* Town Navigation Bar */}
      <TownNavBar 
        towns={towns.map(t => ({ id: t.id, name: t.name, slug: t.slug }))}
        enableMarketValueReport={site?.enable_market_value_report !== false}
        enableMortgageInfo={site?.enable_mortgage_info !== false}
        realtorFirstName={realtor?.first_name}
      />
      
      {/* Area Navigation Bar - Show when only one town exists */}
      {towns.length === 1 && areas.length > 0 && (
        <AreaNavBar areas={areas} townSlug={towns[0].slug} />
      )}
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative bg-gradient-hero text-white py-6 md:py-10">
          <div className="container px-4 sm:px-6 lg:px-8 relative z-10">
            <nav className="flex items-center gap-2 text-sm mb-6 opacity-80">
              <Link to="/" className="hover:underline">Home</Link>
              <span>/</span>
              <span>Towns</span>
            </nav>
            <h1 className="text-2xl md:text-3xl font-display font-bold mb-2">
              Explore Towns in {site?.location_served || 'Our Area'}
            </h1>
            <h2 className="text-2xl md:text-3xl font-display font-medium mb-4 opacity-90">
              Find Your Perfect Neighborhood
            </h2>
            <p className="text-xl opacity-80 max-w-2xl">
              Browse {towns.length} communities across {site?.location_served || 'our service area'}.
            </p>
          </div>
        </section>

        {/* Search */}
        <section className="py-8 bg-muted">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                placeholder="Search towns..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12 text-lg"
              />
            </div>
          </div>
        </section>

        {/* Towns Grid */}
        <section className="py-12 md:py-16">
          <div className="container px-4 sm:px-6 lg:px-8">
            {filteredTowns.length === 0 ? (
              <div className="text-center py-12">
                <MapPin className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">
                  {searchQuery ? 'No towns match your search.' : 'No towns available yet.'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredTowns.map((town) => (
                  <Link key={town.id} to={`/${town.slug}`}>
                    <Card className="h-full hover:shadow-lg transition-shadow">
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <div className="p-3 rounded-full bg-primary/10">
                            <MapPin className="h-6 w-6 text-primary" />
                          </div>
                          <div className="flex-1">
                            <h2 className="text-xl font-semibold mb-1">
                              {town.name}
                              {town.state && <span className="text-muted-foreground font-normal">, {town.state}</span>}
                            </h2>
                            {town.intro_summary && (
                              <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                                {town.intro_summary}
                              </p>
                            )}
                            {(town._areaCount || 0) > 0 && (
                              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                <Building className="h-4 w-4" />
                                {town._areaCount} neighborhood{town._areaCount !== 1 ? 's' : ''}
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer 
        siteName={site?.name || 'Real Estate'} 
        towns={towns} 
        realtor={realtor}
        enableBuyers={true}
        enableSellers={true}
        enableMortgageInfo={site?.enable_mortgage_info !== false}
      />
    </div>
  );
}